import { Tooltip, Button } from '@alphonsocode/kallos';
import JSZip from 'jszip';
import React, { useEffect, useState } from 'react';
import {
  BsArrowCounterclockwise,
  BsDownload,
  BsFileText,
} from 'react-icons/bs';
import { saveAs } from 'file-saver';
import { fetchAssetText } from '../Carousel';
import { LogPreviewModal } from './logPreviewModal';
import { ConfirmModal } from './confirmModal';
import { useRetestMutation } from 'services/api/creativePreview';
import { track } from 'analytics/posthog';
import { useGetConfigQuery } from 'services/api';

const sharedButtonStyle = {
  width: '40px',
  height: '40px',
  marginRight: '5px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: '#006e7d',
  fontWeight: 'bold',
  marginTop: 0,
};

export default function SecondRowButtons({
  data,
  mediaItems,
  testType,
  artifactsVisible = true,
  assetsLoaded = false,
  assetsLoading = false,
  ensureAssetsLoaded = async () => [],
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mediaItems: any[];
  testType: string;
  artifactsVisible?: boolean;
  assetsLoaded?: boolean;
  assetsLoading?: boolean;
  ensureAssetsLoaded?: () => Promise<any[]>;
}) {
  const assetData = data.assets ?? [];
  const isProgrammatic = testType === 'ssp';
  const [progressLogs, setProgressLogs] = useState('');
  const [open, setOpen] = useState(false);
  const [retryModal, setRetryModal] = useState(false);
  const { data: appConfig } = useGetConfigQuery(undefined, { refetchOnMountOrArgChange: true });
  const cdnUrl = appConfig?.cdnUrl || undefined;
  useEffect(() => {
    setProgressLogs('');
  }, [data?._id]);

  useEffect(() => {
    if (!artifactsVisible && data?.status !== 'Error') return;
    if (progressLogs.length > 0) return;

    let cancelled = false;

    const fetchProgressFile = async () => {
      const assets = assetsLoaded
        ? assetData
        : await ensureAssetsLoaded();
      if (cancelled) return;

      const logAsset = assets?.find(
        asset => asset.fileName === 'progress.txt',
      );

      if (logAsset) {
        try {
          const text = await fetchAssetText(cdnUrl, logAsset.fileName, logAsset.path);
          if (!cancelled) setProgressLogs(text ?? '');
        } catch (err) {
          console.error('Failed to fetch log asset:', err);
        }
      }
    };

    fetchProgressFile();

    return () => {
      cancelled = true;
    };
  }, [
    artifactsVisible,
    assetsLoaded,
    assetData,
    ensureAssetsLoaded,
    progressLogs.length,
  ]);

  const buttons = [
    {
      icon: <BsDownload size={20} />,
      tooltip: 'Download All Assets',
      onClick: async () => {
        track('adsure assets downloaded', {
          asset_count: mediaItems.length,
          test_id: data._id,
          surface: 'adsure',
        });
        const zip = new JSZip();
        const folder = zip.folder('assets')!;

        const fetchAndAddToZip = async (url, filename) => {
          try {
            const response = await fetch(url);
            const blob = await response.blob();
            folder.file(filename, blob);
          } catch (err) {
            console.error(`Failed to download ${filename}:`, err);
          }
        };

        const downloadAll = async () => {
          const items = mediaItems?.filter(Boolean) ?? [];
          if (items.length === 0) return;

          await Promise.all(
            items.map((item, i) =>
              fetchAndAddToZip(
                item.url,
                `asset_${i + 1}.${item.type === 'image' ? 'jpeg' : 'mp4'}`,
              ),
            ),
          );

          const zipBlob = await zip.generateAsync({ type: 'blob' });
          saveAs(zipBlob, 'all_assets.zip');
        };
        await downloadAll();
      },
      disabled:
        !mediaItems ||
        mediaItems.length === 0 ||
        mediaItems.some(item => item?.isLoading),
    },
    {
      icon: <BsFileText size={20} />,
      tooltip: 'View Logs',
      onClick: () => {
        track('adsure logs opened', {
          test_id: data._id,
          status: data.status,
          adsure_test_tag: testType,
          surface: 'adsure',
        });
        setOpen(true);
      },
      disabled: !(progressLogs.length > 0),
    },
    {
      icon: <BsArrowCounterclockwise size={20} />,
      tooltip: isProgrammatic
        ? 'Retest is not supported'
        : 'Retest',
      onClick: () => {
        if (isProgrammatic) return;
        track('adsure retest initiated', {
          test_id: data._id,
          status: data.status,
          adsure_test_tag: testType,
          surface: 'adsure',
        });
        setRetryModal(true);
      },
      disabled: isProgrammatic || data.status === 'Unsupported Test',
    },
  ];
  return (
    <div className="d-flex">
      {buttons.map((btn, index) => (
        <Tooltip
          key={index}
          overlay={btn.tooltip}
          openDelay={0}
          closeDelay={0}
          arrow={false}
          style={{
            maxWidth: '300px',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            marginTop: 5,
          }}
        >
          <Button
            variant="borderless"
            onClick={btn.onClick} // Use the specific onClick function for each button
            style={sharedButtonStyle}
            disabled={btn.disabled ?? false}
          >
            <span style={{ strokeWidth: 2 }}>{btn.icon}</span>
          </Button>
        </Tooltip>
      ))}
      {open && (
        <LogPreviewModal open={open} setOpen={setOpen} logs={progressLogs} />
      )}
      {retryModal && (
        <ConfirmModal
          open={retryModal}
          setOpen={setRetryModal}
          data={data}
          testType={testType}
        />
      )}
    </div>
  );
}
